
import { useEffect, useState } from "react";
import Modal from "../../../components/Modal"
import { FileUploadView } from "./components/FileUploadView";
import { List } from "./components/List";
import { Button } from "../../../components/Button";
import { uploadFileToS3 } from "../../praxis-call/utils";
import { useApp } from "../../../services/app-context";
import { useUser } from "../../../services/user-context";
import { api } from "../../../services/api";
import { ShowToast } from "../../../services/toast";

export function generateFileNameWithUUID(name: string) {
    if (!name) {
        return name;
    }
    const uuid = crypto.randomUUID();
    // Attach uuid to filename, keeping the extension at the end
    const lastDotIndex = name.lastIndexOf('.');
    if (lastDotIndex === -1) {
        // No extension found, just append uuid
        return `${uuid}`;
    }
    const base = name.substring(0, lastDotIndex);
    const ext = name.substring(lastDotIndex); // includes the dot
    return `${uuid}${ext}`;
}

/** Cohort entries from `tenantProfile.groups` — passed through List → ListItem for tags and selects. */
export type TenantProfileGroup = {
    key: string;
    name: string;
    backgroundColor?: string;
    textColor?: string;
};

export type FileType = {
    file: File;
    id: string;
    type: string;
    category: string;
    name: string;
    code: string;
    groups: string[];
}

export function SalesAidModal({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void, onSuccess: () => void }) {
    const { tenantProfile } = useApp();
    const { credentials } = useUser() as any;
    const [files, setFiles] = useState<FileType[]>([]);
    const [loading, setLoading] = useState(false);
    const [fileUploadingProgress, setFileUploadingProgress] = useState<{ file: File, progress: string }[]>([]);
    const fileTypes = ["PDF"];


    const handleUpload = (files: File[]) => {
        // setFiles(files);
        const list: FileType[] = [];
        Array.from(files).forEach(file => {
            list.push({
                file,
                id: file.name,
                type: file.type.split("/")[1],
                category: "Product Efficacy",
                name: file.name,
                code: "",
                groups: [],
            });
        });
        setFiles(prev => [...prev, ...list]);
    };

    // The uploader rejects a whole batch if any file isn't a PDF, and reports it once per bad file.
    const handleTypeError = () => {
        ShowToast({
            id: "sales-aid-file-type",
            type: "error",
            message: "Only PDF files can be added. Nothing from that selection was added.",
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const uploadedFiles: { s3Key: string, category: string, name: string, code: string, groups: string[] }[] = [];
            for (const file of files) {
                const isAlreadyUploaded = uploadedFiles.some(f => f.s3Key === `app${file.file.name}`);
                if (isAlreadyUploaded) {
                    continue;
                }
                setFileUploadingProgress(prev => [...prev, { file: file.file, progress: "progress" }]);
                const fileName = generateFileNameWithUUID(file.name)
                const response = await uploadFileToS3(file.file, fileName, tenantProfile?.assets_bucket, credentials);
                if (response) {
                    const key = response.split("/app")?.[1]?.split("?")?.[0];
                    if (key) {
                        setFileUploadingProgress(prev => {
                            const index = prev.findIndex(f => f.file.name === file.file.name);
                            if (index !== -1) {
                                prev[index].progress = "completed";
                            }
                            return prev;
                        })
                        uploadedFiles.push({ s3Key: `app${key}`, category: file.category, name: file.name, code: file.code, groups: file.groups });
                    } else {
                        setFileUploadingProgress(prev => {
                            const index = prev.findIndex(f => f.file.name === file.file.name);
                            if (index !== -1) {
                                prev[index].progress = "failed";
                            }
                            return prev;
                        })
                    }
                } else {
                    setFileUploadingProgress(prev => {
                        const index = prev.findIndex(f => f.file.name === file.file.name);
                        if (index !== -1) {
                            prev[index].progress = "failed";
                        }
                        return prev;
                    })
                }
            }
            const response = await api.uploadSalesAidFiles({ files: uploadedFiles });
            onSuccess();
            setLoading(false);

        } catch (error) {
            setLoading(false);
        }
    };

    const handleDelete = (file: FileType) => {
        setFiles(prev => prev.filter(f => f.id !== file.id));
    };

    const handleChange = (file: FileType) => {
        setFiles(prev => prev.map(f => f.id === file.id ? file : f));
    };

    useEffect(() => {
        if (isOpen) {
            setFiles([]);
        }
    }, [isOpen]);

    return (
        <Modal
            contentClassName="!px-0 !w-[50vw]"
            title="Add document"
            isOpen={isOpen}
            handleModal={onClose}
        >
            <div className="px-6 mb-4">
                <span className="text-sm text-[#1C1B18]">Upload files and assign them to a cohort for reps to access.</span>
            </div>
            <FileUploadView onChange={handleUpload} onTypeError={handleTypeError} fileTypes={fileTypes} disabled={loading} />
            <List files={files} onDelete={handleDelete} fileUploadingProgress={fileUploadingProgress} isLoading={loading} onChange={handleChange} groupOptions={(tenantProfile?.groups || [])} />
            <div className="px-6 flex justify-end gap-2.5 mt-5">
                <Button variant="primary" onClick={onClose} disabled={loading} className="!rounded-full !text-black !w-24 hover:!opacity-80 !py-2.5 !bg-gray-201">Cancel</Button>
                <Button variant="primary" className="!rounded-full !py-2.5" onClick={handleSave} disabled={loading || files.length === 0} loading={loading}>Add to Sales Aid Panel</Button>
            </div>
        </Modal>
    );
}